<template>
  <Dashboard :title="$t('play.play_now.title')" :no-padding="true" :no-padding-bottom="true">
    <SubscribeContent v-if="showUpgrade" />
    <div v-else class="select-none flex flex-col gap-1.5 relative w-full pt-2 min-h-full">
      <!-- Header section - sticky for all tabs -->
      <div class="sticky top-0 z-50 bg-gray-950 px-9 pt-6 -mt-2 pb-4">
        <h1 class="text-[24px] font-semibold text-text-primary mb-4">{{ $t('play.play_now.select_mode') }}</h1>

        <!-- Navbar -->
        <nav class="w-full flex flex-row gap-5 relative border-b border-gray-800">
          <div
            ref="coachDiv"
            class="py-2 text-center cursor-pointer flex gap-2 items-center w-full lg:w-auto justify-center lg:justify-start"
            @click="updateActiveTab('coach')"
          >
            <img
              src="../assets/icons/coach.svg"
              :class="[activeTab === 'coach' ? 'brightness-200' : '']"
              class="h-4 w-4 duration-300 select-none"
              :alt="$t('play.play_now.sparkle_icon_alt')"
            />
            <h3
              class="leading-5 duration-300 select-none"
              :class="[
                activeTab === 'coach' ? 'text-text-primary' : 'text-text-secondary',
              ]"
            >
              {{ $t('play.play_now.tab_coach') }}
            </h3>
          </div>
          <div
            ref="favoritesDiv"
            class="py-2 text-center cursor-pointer flex gap-2 items-center w-full lg:w-auto justify-center lg:justify-start"
            @click="updateActiveTab('favorites')"
          >
            <img
              src="../assets/icons/heart.svg"
              :class="[activeTab === 'favorites' ? 'brightness-200' : '']"
              class="h-4 w-4 duration-300 select-none"
              :alt="$t('play.play_now.heart_icon_alt')"
            />
            <h3
              class="leading-5 duration-300 select-none"
              :class="[
                activeTab === 'favorites' ? 'text-text-primary' : 'text-text-secondary',
              ]"
            >
              {{ $t('play.play_now.tab_favorites') }}
            </h3>
          </div>
          <div
            ref="browseDiv"
            class="py-2 text-center cursor-pointer flex gap-2 items-center w-full lg:w-auto justify-center lg:justify-start"
            @click="updateActiveTab('browse')"
          >
            <img
              src="../assets/icons/navigation.svg"
              :class="[activeTab === 'browse' ? 'brightness-200' : '']"
              class="h-4 w-4 duration-300 select-none"
              :alt="$t('play.play_now.navigation_icon_alt')"
            />
            <h3
              class="leading-5 duration-300 select-none"
              :class="[
                activeTab === 'browse' ? 'text-text-primary' : 'text-text-secondary',
              ]"
            >
              {{ $t('play.play_now.tab_browse') }}
            </h3>
          </div>
          <div class="absolute flex flex-row bottom-0">
            <div
              class="h-[2px] grow-transition"
              :style="{ width: `${getSectionUnderlinePadding()}px` }"
            />
            <div
              ref="sectionUnderline"
              class="h-[2px] bg-primary-600 grow-transition"
              :style="{ width: `${getSectionUnderlineWidth()}px` }"
            />
          </div>
        </nav>

        <!-- Browse search bar - only show on browse tab -->
        <div v-if="activeTab === 'browse'" class="border-b border-gray-800 py-4">
          <div class="flex flex-row gap-4">
            <div class="grid grid-cols-1 lg:grid-cols-5 gap-3 w-full">
              <div class="flex justify-center lg:justify-end w-full col-span-1 lg:col-span-2">
                <form action="" class="relative mx-auto w-full">
                  <input
                    type="search"
                    v-model="browseSearchInput"
                    :placeholder="$t('play.play_now.search_placeholder')"
                    class="peer cursor-pointer text-sm font-light relative z-10 h-8 w-full rounded-md bg-gray-800 border-gray-700 focus:border-gray-700 focus:outline-none focus:ring-0 pl-8 outline-none text-text-primary placeholder:text-text-secondary"
                  />
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="absolute top-0 left-0 z-10 ml-2 translate-y-1/2 h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="#9CADC4"
                    stroke-width="2"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </form>
              </div>

              <Menu as="div" class="relative inline-block text-left w-full md:col-span-1">
                <MenuButton
                  class="relative w-full disabled:bg-gray-400 inline-flex items-center px-2 py-2 border border-gray-700 shadow-sm text-sm font-medium rounded-md text-text-primary bg-gray-800"
                  :class="browseActiveCategories.length > 1 ? 'min-h-8' : 'h-8'"
                >
                  <div class="flex-1 text-left font-light flex flex-wrap gap-1 items-center min-w-0">
                    <span class="text-text-secondary pr-1 flex-shrink-0 text-xs md:text-sm">{{ $t('play.play_now.filter_categories') }}</span>
                    <span v-if="browseActiveCategories.length === 0" class="text-xs md:text-sm">{{ $t('common.all') }}</span>
                    <span v-for="category in browseActiveCategories" :key="category" class="text-text-primary text-xs py-1 px-2 bg-gray-700 rounded-full flex flex-row gap-1 items-center">
                      {{ category }}
                      <XMarkIcon class="h-3 w-3 ml-auto text-primary-400 flex-shrink-0" @click.stop="updateBrowseActiveCategories(category)" />
                    </span>
                  </div>
                  <ChevronDownIcon class="-mr-1 ml-2 md:ml-4 h-4 w-4 md:h-5 md:w-5 flex-shrink-0" aria-hidden="true" />
                </MenuButton>

                <transition
                  enter-active-class="transition ease-out duration-100"
                  enter-from-class="transform opacity-0 scale-95"
                  enter-to-class="transform opacity-100 scale-100"
                  leave-active-class="transition ease-in duration-75"
                  leave-from-class="transform opacity-100 scale-100"
                  leave-to-class="transform opacity-0 scale-95"
                >
                  <MenuItems
                    class="absolute left-0 md:right-0 z-50 mt-2 w-full origin-top-left md:origin-top-right divide-y divide-gray-100 rounded-md bg-gray-800 border border-gray-700 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
                  >
                    <div class="py-1 flex flex-col px-4">
                      <MenuItem
                        :key="category"
                        v-for="category in browseCategoryNames"
                        v-slot="{ active }"
                        @click="updateBrowseActiveCategories(category)"
                      >
                        <a
                          href="#"
                          :class="[
                            active ? 'text-text-primary' : 'text-text-secondary',
                            'group flex items-center py-2 text-sm w-full justify-start',
                          ]"
                        >
                          {{ category }}
                          <CheckIcon v-if="browseActiveCategories.includes(category)" class="h-5 w-5 ml-auto text-primary-400" />
                        </a>
                      </MenuItem>
                    </div>
                  </MenuItems>
                </transition>
              </Menu>

              <Menu as="div" class="relative inline-block text-left w-full md:col-span-1">
                <MenuButton
                  class="relative w-full disabled:bg-gray-400 inline-flex items-center px-2 py-2 border border-gray-700 shadow-sm text-sm font-medium rounded-md text-text-primary bg-gray-800"
                  :class="browseActivePlayerCounts.length > 1 ? 'min-h-8' : 'h-8'"
                >
                  <div class="flex-1 text-left font-light flex flex-wrap gap-1 items-center min-w-0">
                    <span class="text-text-secondary pr-1 flex-shrink-0 text-xs md:text-sm">{{ $t('play.play_now.filter_players') }}</span>
                    <span v-if="browseActivePlayerCounts.length === 0" class="text-xs md:text-sm">{{ $t('common.all') }}</span>
                    <span v-for="playerCountRange in browseActivePlayerCounts" :key="playerCountRange" class="text-text-primary text-xs py-1 px-2 bg-gray-700 rounded-full flex flex-row gap-1 items-center">
                      {{ playerCountRange.min }}-{{ playerCountRange.max }}
                      <XMarkIcon class="h-3 w-3 ml-auto text-primary-400 flex-shrink-0" @click.stop="updateBrowseActivePlayerCounts(playerCountRange)" />
                    </span>
                  </div>
                  <ChevronDownIcon class="-mr-1 ml-2 md:ml-4 h-4 w-4 md:h-5 md:w-5 flex-shrink-0" aria-hidden="true" />
                </MenuButton>

                <transition
                  enter-active-class="transition ease-out duration-100"
                  enter-from-class="transform opacity-0 scale-95"
                  enter-to-class="transform opacity-100 scale-100"
                  leave-active-class="transition ease-in duration-75"
                  leave-from-class="transform opacity-100 scale-100"
                  leave-to-class="transform opacity-0 scale-95"
                >
                  <MenuItems
                    class="absolute left-0 md:right-0 z-50 mt-2 w-full origin-top-left md:origin-top-right divide-y divide-gray-100 rounded-md bg-gray-800 border border-gray-700 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
                  >
                    <div class="py-1 flex flex-col px-4">
                      <MenuItem
                        :key="playerCountRange"
                        v-for="playerCountRange in browsePlayerCountRanges"
                        v-slot="{ active }"
                        @click="updateBrowseActivePlayerCounts(playerCountRange)"
                      >
                        <a
                          href="#"
                          :class="[
                            active ? 'text-text-primary' : 'text-text-secondary',
                            'group flex items-center py-2 text-sm w-full justify-start',
                          ]"
                        >
                          {{ playerCountRange.min }}-{{ playerCountRange.max }}
                          <CheckIcon v-if="browseActivePlayerCounts.includes(playerCountRange)" class="h-5 w-5 ml-auto text-primary-400" />
                        </a>
                      </MenuItem>
                    </div>
                  </MenuItems>
                </transition>
              </Menu>

              <Menu as="div" class="relative inline-block text-left w-full md:col-span-1">
                <MenuButton
                  class="relative w-full disabled:bg-gray-400 inline-flex items-center px-2 py-2 border border-gray-700 shadow-sm text-sm font-medium rounded-md text-text-primary bg-gray-800"
                  :class="browseActiveTags.length > 1 ? 'min-h-8' : 'h-8'"
                >
                  <div class="flex-1 text-left font-light flex flex-wrap gap-1 items-center min-w-0">
                    <span class="text-text-secondary pr-1 flex-shrink-0 text-xs md:text-sm">{{ $t('play.play_now.filter_tags') }}</span>
                    <span v-if="browseActiveTags.length === 0" class="text-xs md:text-sm">{{ $t('common.all') }}</span>
                    <span v-for="tag in browseActiveTags" :key="tag" class="text-text-primary text-xs py-1 px-2 bg-gray-700 rounded-full flex flex-row gap-1 items-center">
                      {{ tag }}
                      <XMarkIcon class="h-3 w-3 ml-auto text-primary-400 flex-shrink-0" @click.stop="updateBrowseActiveTags(tag)" />
                    </span>
                  </div>
                  <ChevronDownIcon class="-mr-1 ml-2 md:ml-4 h-4 w-4 md:h-5 md:w-5 flex-shrink-0" aria-hidden="true" />
                </MenuButton>

                <transition
                  enter-active-class="transition ease-out duration-100"
                  enter-from-class="transform opacity-0 scale-95"
                  enter-to-class="transform opacity-100 scale-100"
                  leave-active-class="transition ease-in duration-75"
                  leave-from-class="transform opacity-100 scale-100"
                  leave-to-class="transform opacity-0 scale-95"
                >
                  <MenuItems
                    class="absolute left-0 md:right-0 z-50 mt-2 w-full origin-top-left md:origin-top-right divide-y divide-gray-100 rounded-md bg-gray-800 border border-gray-700 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none max-h-64 overflow-y-auto"
                  >
                    <div class="py-1 flex flex-col px-4">
                      <MenuItem
                        :key="tag"
                        v-for="tag in browseAvailableTags"
                        v-slot="{ active }"
                        @click="updateBrowseActiveTags(tag)"
                      >
                        <a
                          href="#"
                          :class="[
                            active ? 'text-text-primary' : 'text-text-secondary',
                            'group flex items-center py-2 text-sm w-full justify-start',
                          ]"
                        >
                          {{ tag }}
                          <CheckIcon v-if="browseActiveTags.includes(tag)" class="h-5 w-5 ml-auto text-primary-400" />
                        </a>
                      </MenuItem>
                    </div>
                  </MenuItems>
                </transition>
              </Menu>
            </div>
          </div>
        </div>
      </div>

      <!-- Content -->
      <div>
        <!-- Favorites -->
        <div v-if="activeTab === 'favorites'" class="px-4 md:px-9">
          <PlayNowFavorites
            v-if="mods.length > 0"
            ref="playNowFavoritesRef"
            @selectMod="selectMod"
            :selectedMod="selectedMod"
            :modCategories="modCategories"
            :isDataLoaded="true"
          />
          <PlayNowFavoritesSkeleton v-else />
        </div>

        <!-- Coach -->
        <div v-if="activeTab === 'coach'" class="px-4 md:px-9">
          <PlayNowForYou
            v-if="mods.length > 0 && !statsLoading"
            ref="playNowForYouRef"
            @selectMod="selectMod"
            :selectedMod="selectedMod"
            :fiveWorstStats="fiveWorstStats"
            :fiveBestStats="fiveBestStats"
            :modCategories="modCategories"
            :isDataLoaded="!statsLoading"
          />
          <PlayNowForYouSkeleton v-else />
        </div>

        <!-- Browse -->
        <div v-if="activeTab === 'browse'" class="flex-1 px-9">
          <PlayNowBrowse
            v-if="mods.length > 0 && modCategories.length > 0"
            ref="playNowBrowseRef"
            :modCategories="modCategories"
            @selectMod="selectMod"
            :selectedMod="selectedMod"
            :searchInput="browseSearchInput"
            :activeCategories="browseActiveCategories"
            :activePlayerCounts="browseActivePlayerCounts"
            :activeTags="browseActiveTags"
          />
          <PlayNowBrowseSkeleton v-else />
        </div>

        <!-- Bottom spacing to prevent scroll jumping on last row -->
        <div class="mb-32" />
      </div>

      <!-- Server info menu -->
      <div class="sticky bottom-0 left-0 w-full flex items-center justify-center z-40 mt-auto">
        <div class="w-full min-h-[98px] fade-bg flex items-end justify-center pb-8">
          <div class="flex flex-row gap-6 p-3 bg-gray-900 rounded-lg border border-gray-800 items-center shadow-lg">
            <div class="flex flex-row gap-2 items-center justify-center">
              <Menu as="div" class="relative inline-block text-left hidden sm:block w-full lg:w-auto" v-slot="{ open }">
                <MenuButton
                  class="relative w-full lg:w-auto disabled:bg-gray-400 inline-flex h-8 items-center px-2 py-2 shadow-sm text-sm font-medium rounded-md text-text-primary hover:bg-gray-700"
                  :class="open ? 'bg-gray-800 ring-1 ring-gray-700' : 'bg-gray-900'"
                >
                  <div class="flex-1 text-left font-light flex gap-1 items-center">
                    <span class="text-text-secondary hidden lg:block">{{ $t('play.shared.mode_label') }}</span>
                    <img v-if="selectedMod" :src="selectedModIconSrc" class="h-5 w-5">
                    <span v-if="selectedMod">{{ selectedMod.friendly_name }}</span>
                    <span v-else>{{ $t('common.all') }}</span>
                  </div>
                  <ChevronDownIcon :class="open ? 'rotate-180' : ''" class="-mr-1 ml-4 h-5 w-5 transition-all duration-300" aria-hidden="true" />
                </MenuButton>

                <transition
                  enter-active-class="transition ease-out duration-100"
                  enter-from-class="transform opacity-0 scale-95"
                  enter-to-class="transform opacity-100 scale-100"
                  leave-active-class="transition ease-in duration-75"
                  leave-from-class="transform opacity-100 scale-100"
                  leave-to-class="transform opacity-0 scale-95"
                >
                  <MenuItems
                    class="menu-hover-highlight absolute left-0 z-50 min-w-[240px] max-h-[420px] overflow-y-auto bottom-full mb-5 w-full lg:w-[192px] origin-bottom-left divide-y divide-gray-100 rounded-md bg-gray-900 border border-gray-800 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
                  >
                    <div class="flex flex-col py-4">
                      <h1 class="text-text-primary px-4 mb-4">{{ $t('play.play_now.select_mode') }}</h1>

                      <MenuItem
                        :key="modWithCategory.mod"
                        v-for="modWithCategory in allMods"
                        v-slot="{ active }"
                        @click="(e) => { e.preventDefault(); selectMod(modWithCategory.mod); }"
                      >
                        <a
                          href="#"
                          :class="[
                            selectedMod?.name === modWithCategory.mod.name ? 'bg-gray-800' : 'bg-gray-900',
                            'group flex items-center py-2 text-sm w-full justify-start text-text-primary px-4',
                          ]"
                        >
                          <img :src="modCategoryIconSrc(modWithCategory.category)" class="w-5 h-5" />
                          {{ modWithCategory.mod.friendly_name }}
                          <CheckIcon v-if="modWithCategory.mod.name === selectedMod?.name" class="h-5 w-5 ml-auto text-primary-400" />
                        </a>
                      </MenuItem>
                    </div>
                  </MenuItems>
                </transition>
              </Menu>

              <Menu as="div" class="relative inline-block text-left w-full lg:w-auto" v-slot="{ open }">
                <MenuButton
                  class="relative w-full lg:w-auto disabled:bg-gray-400 inline-flex h-8 items-center px-2 py-2 shadow-sm text-sm font-medium rounded-md text-text-primary hover:bg-gray-700"
                  :class="open ? 'bg-gray-800 ring-1 ring-gray-700' : 'bg-gray-900'"
                >
                  <div class="flex-1 text-left font-light flex gap-1 items-center">
                    <span class="text-text-secondary hidden lg:block">{{ $t('play.shared.map_label') }}</span>
                    <img v-if="selectedMap" :src="`/maps/${selectedMap?.name}_pin.png`" class="h-4 w-4" />
                    <span v-if="selectedMap">{{ selectedMap.friendly_name }}</span>
                    <span v-else>{{ $t('common.all') }}</span>
                  </div>
                  <ChevronDownIcon :class="open ? 'rotate-180' : ''" class="-mr-1 ml-4 h-5 w-5 transition-all duration-300" aria-hidden="true" />
                </MenuButton>

                <transition
                  enter-active-class="transition ease-out duration-100"
                  enter-from-class="transform opacity-0 scale-95"
                  enter-to-class="transform opacity-100 scale-100"
                  leave-active-class="transition ease-in duration-75"
                  leave-from-class="transform opacity-100 scale-100"
                  leave-to-class="transform opacity-0 scale-95"
                >
                  <MenuItems
                    class="menu-hover-highlight absolute left-0 z-50 min-w-[240px] max-h-[420px] overflow-y-auto bottom-full mb-5 w-full lg:w-[192px] origin-bottom-left divide-y divide-gray-100 rounded-md bg-gray-900 border border-gray-800 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
                  >
                    <div class="flex flex-col py-4 gap-4">
                      <h1 class="text-text-primary px-4">{{ $t('play.play_now.select_map') }}</h1>
                      <div class="px-4">
                        <input type="text" v-model="mapsSearchQuery" class="w-full bg-gray-800 border border-gray-700 rounded-md p-2 text-sm placeholder:text-text-secondary placeholder:font-light placeholder:text-sm text-text-primary" :placeholder="$t('play.play_now.search_placeholder')" />
                      </div>

                      <div class="flex flex-col">
                        <MenuItem
                          :key="map"
                          v-for="map in filteredMaps"
                          v-slot="{ active }"
                          @click="(e) => { e.preventDefault(); updateSelectedMap(map); }"
                        >
                          <a
                            href="#"
                            :class="[
                              selectedMap?.name === map.name ? 'bg-gray-800' : 'bg-gray-900',
                              'group flex items-center py-2 text-sm w-full justify-start text-text-primary px-4',
                            ]"
                          >
                            {{ map.friendly_name }}
                            <CheckIcon v-if="map.name === selectedMap?.name" class="h-5 w-5 ml-auto text-primary-400" />
                          </a>
                        </MenuItem>
                      </div>

                    </div>
                  </MenuItems>
                </transition>
              </Menu>

              <Menu as="div" class="relative inline-block text-left w-full lg:w-auto" v-slot="{ open }">
                <MenuButton
                  class="relative w-full lg:w-auto disabled:bg-gray-400 inline-flex h-8 items-center px-2 py-2 shadow-sm text-sm font-medium rounded-md text-text-primary hover:bg-gray-700"
                  :class="open ? 'bg-gray-800 ring-1 ring-gray-700' : 'bg-gray-900'"
                >
                  <div class="flex-1 text-left font-light flex gap-1 items-center">
                    <span class="text-text-secondary hidden lg:block">{{ $t('play.play_now.location_label') }}</span>
                    <img v-if="selectedLocation?.flag" :src="`https://flagcdn.com/w20/${selectedLocation.flag.toLowerCase()}.png`" class="h-4 w-4 rounded-full" :alt="$t('play.play_now.location_flag_alt')" />
                    <span v-if="selectedLocation">{{ selectedLocation.name }}</span>
                    <span v-else>{{ $t('common.all') }}</span>
                  </div>
                  <ChevronDownIcon :class="open ? 'rotate-180' : ''" class="-mr-1 ml-4 h-5 w-5 transition-all duration-300" aria-hidden="true" />
                </MenuButton>

                <transition
                  enter-active-class="transition ease-out duration-100"
                  enter-from-class="transform opacity-0 scale-95"
                  enter-to-class="transform opacity-100 scale-100"
                  leave-active-class="transition ease-in duration-75"
                  leave-from-class="transform opacity-100 scale-100"
                  leave-to-class="transform opacity-0 scale-95"
                >
                  <MenuItems
                    class="menu-hover-highlight absolute left-0 z-50 min-w-[240px] max-h-[420px] overflow-y-auto bottom-full mb-5 w-full lg:w-[192px] origin-bottom-left divide-y divide-gray-100 rounded-md bg-gray-900 border border-gray-800 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
                  >
                    <div class="flex flex-col py-4 gap-4">
                      <h1 class="text-text-primary px-4">{{ $t('play.play_now.select_location') }}</h1>
                      <div class="px-4">
                        <input type="text" v-model="locationsSearchQuery" class="w-full bg-gray-800 border border-gray-700 rounded-md p-2 text-sm placeholder:text-text-secondary placeholder:font-light placeholder:text-sm text-text-primary" :placeholder="$t('play.play_now.search_placeholder')" />
                      </div>

                      <div class="flex flex-col">
                        <MenuItem
                          :key="location"
                          v-for="location in filteredLocations"
                          v-slot="{ active }"
                          @click="(e) => { e.preventDefault(); updateSelectedLocation(location); }"
                        >
                          <a
                            href="#"
                            :class="[
                              selectedLocation?.value === location.value ? 'bg-gray-800' : 'bg-gray-900',
                              'group flex items-center py-2 text-sm w-full justify-start text-text-primary px-4',
                            ]"
                          >
                            <img v-if="location.flag" :src="`https://flagcdn.com/w20/${location.flag.toLowerCase()}.png`" class="h-4 w-4 rounded-full mr-2" :alt="$t('play.play_now.location_flag_alt')" />
                            <span class="flex-1">{{ location.name }}</span>
                            <span class="text-text-secondary">{{ latencyLabel(location) }}</span>
                            <CheckIcon v-if="location.value === selectedLocation?.value" class="h-5 w-5 ml-2 text-primary-400" />
                          </a>
                        </MenuItem>
                      </div>

                    </div>
                  </MenuItems>
                </transition>
              </Menu>
            </div>

            <div class="h-[28px] bg-gray-800 w-px hidden lg:block" />

            <div class="flex flex-row gap-2 items-center justify-center">
              <Menu as="div" class="relative inline-block text-left lg:w-auto" v-slot="{ open }">
                <MenuButton
                  class="relative disabled:bg-gray-400 inline-flex h-8 min-w-8 w-8 items-center px-2 py-2 shadow-sm text-sm font-medium rounded-md text-text-primary hover:bg-gray-700"
                  :class="open ? 'bg-gray-700' : 'bg-gray-800'"
                >
                  <div class="flex-1 text-left font-light flex gap-1 items-center">
                    <img src="../assets/icons/more-alt.svg" class="h-4 w-4" :alt="$t('play.play_now.beta_icon_alt')" />
                  </div>
                </MenuButton>

                <transition
                  enter-active-class="transition ease-out duration-100"
                  enter-from-class="transform opacity-0 scale-95"
                  enter-to-class="transform opacity-100 scale-100"
                  leave-active-class="transition ease-in duration-75"
                  leave-from-class="transform opacity-100 scale-100"
                  leave-to-class="transform opacity-0 scale-95"
                >
                  <MenuItems
                    class="absolute right-0 z-50 min-w-[200px] bottom-full mb-5 w-full lg:w-[200px] origin-bottom-right divide-y divide-gray-100 rounded-md bg-gray-900 border border-gray-800 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
                  >
                    <div class="flex flex-col py-4 px-4">
                      <div class="flex items-center justify-between py-2">
                        <h2 class="text-sm text-text-primary">{{ $t('play.play_now.beta_server') }}</h2>
                        <Switch
                          v-model="betaServer"
                          :class="betaServer ? 'bg-primary-500' : 'bg-gray-600'"
                          class="relative inline-flex h-5 w-9 items-center rounded-full"
                        >
                          <span class="sr-only">{{ $t('play.play_now.enable_beta_server') }}</span>
                          <span
                            :class="betaServer ? 'translate-x-4 bg-primary-50' : 'translate-x-[2px] bg-gray-800'"
                            class="inline-block h-4 w-4 transform rounded-full transition"
                          />
                        </Switch>
                      </div>
                    </div>
                  </MenuItems>
                </transition>
              </Menu>

              <button
                class="min-w-8 h-8 w-8 md:w-full whitespace-nowrap md:py-2 md:px-6 duration-200 cursor-pointer rounded-md flex justify-center text-text-primary text-xs items-center"
                :class="modeLaunched ? 'bg-green-600 hover:bg-green-500' : 'bg-danger-700 hover:bg-danger-600'"
                @click="handleServerAction()"
                :disabled="serverLoading || modeLaunched"
                type="button">
                <!-- Success thumbs up icon -->
                <svg v-if="modeLaunched" class="w-3 h-3 mr-1 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                </svg>
                <!-- Loading spinner -->
                <div v-else-if="serverLoading" class="mr-1">
                  <svg
                    class="w-3 h-3 text-white animate-spin"
                    fill="none"
                    viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                    <path
                      class="opacity-75"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      fill="currentColor" />
                  </svg>
                </div>
                <!-- Play icon -->
                <img v-else src="../assets/icons/play.svg" class="w-3 h-3 min-w-3 min-h-3 md:mr-1 brightness-200">
                <h1 class="hidden md:block">{{ modeLaunched ? $t('play.play_now.launched') : buttonText }}</h1>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Dashboard>
</template>

<script setup>
import { CheckIcon, ChevronDownIcon, XMarkIcon } from '@heroicons/vue/24/outline';
import {
  Menu, MenuButton, MenuItem, MenuItems, Switch,
} from '@headlessui/vue';
import {
  ref, computed, watch, onMounted, onUnmounted, nextTick,
} from 'vue';
import { useHead } from '@unhead/vue';
import { useAuth } from '@websanova/vue-auth/src/v3.js';
import { useStore } from 'vuex';
import axios from 'axios';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import PlayNowForYou from '../components/PlayNow/PlayNowForYou.vue';
import PlayNowBrowse from '../components/PlayNow/PlayNowBrowse.vue';
import PlayNowFavorites from '../components/PlayNow/PlayNowFavorites.vue';
import PlayNowForYouSkeleton from '../components/PlayNow/PlayNowForYouSkeleton.vue';
import PlayNowBrowseSkeleton from '../components/PlayNow/PlayNowBrowseSkeleton.vue';
import PlayNowFavoritesSkeleton from '../components/PlayNow/PlayNowFavoritesSkeleton.vue';
import Dashboard from '../components/layouts/DashboardLayout.vue';
import {
  favoriteModIds,
  loadUserPreferences,
  playNowActiveTab,
  playNowMapId,
  playNowModId,
  preferencesLoaded,
  preferencesLoading,
  resolveServerLocation,
  serverLocationId,
  setPlayNowActiveTab,
  setPlayNowMap,
  setPlayNowMod,
  setServerLocation,
  updatePreferences,
} from '../utils/user_preferences.js';
import {
  latencyLabel, loadServerLocations, serverLocations, sortByLatency,
} from '../utils/server_locations.js';
import SubscribeContent from '../components/SubscribeContent.vue';
import currentModName from '../utils/mod_renames';
import { fetchCoachProfile, rsparseStatLabel } from '../utils/coach_pipeline.js';

const { t } = useI18n();
const auth = useAuth();
const user = ref(auth.user());
const showUpgrade = ref(user.value?.access_level < 1);
// Held locally as well as stored, so the tab still answers a click while the
// preferences are unreadable. Same shape as `localSelectedLocation` elsewhere.
const localActiveTab = ref(null);
const activeTab = computed({
  get: () => localActiveTab.value ?? playNowActiveTab.value ?? 'coach',
  set: (value) => {
    localActiveTab.value = value;
    setPlayNowActiveTab(value);
  },
});

// Picks a tab from what content exists, not from a click, so it is never saved:
// a player with no matches would otherwise keep Browse after the Coach tab has
// recommendations for them. Same as the v5 page's fallback.
function applyDefaultTab(value) {
  localActiveTab.value = value;
}
const sectionUnderlineWidth = ref(76.6);
const sectionUnderlinePadding = ref(0);
const favoritesDiv = ref(null);
const coachDiv = ref(null);
const browseDiv = ref(null);
const playNowFavoritesRef = ref(null);
const playNowForYouRef = ref(null);
const playNowBrowseRef = ref(null);
const selectedMap = ref(null);
const selectedMod = ref(null);
const selectedLocation = ref(null);
const store = useStore();
const mapsSearchQuery = ref('');
const locationsSearchQuery = ref('');
// One launch only, never stored: off on every mount and after each accepted
// launch.
const betaServer = ref(false);
const modCategories = ref([]);
const serverLoading = ref(false);
const modeLaunched = ref(false);
const route = useRoute();
const router = useRouter();

// Browse search and filter state
const browseSearchInput = ref('');
const browseActiveCategories = ref([]);
const browseActivePlayerCounts = ref([]);
const browseActiveTags = ref([]);

const browsePlayerCountRanges = [
  { min: 1, max: 2 },
  { min: 3, max: 4 },
  { min: 5, max: 10 },
];

const browseCategoryNames = computed(() => modCategories.value.map((category) => category.name));

const browseAvailableTags = computed(() => {
  const tagSet = new Set();
  modCategories.value.forEach((category) => {
    category.mods.forEach((mod) => {
      if (mod.playable && mod.cs2 && mod.tags && Array.isArray(mod.tags)) {
        if (mod.name && mod.name.toLowerCase() === 'scrim' && (!user.value || user.value.access_level <= 1)) {
          return;
        }
        mod.tags.forEach((tag) => {
          if (tag.name) {
            tagSet.add(tag.name);
          }
        });
      }
    });
  });
  return Array.from(tagSet).sort();
});

const browseCategoriesLabel = computed(() => {
  if (browseActiveCategories.value.length === 0) {
    return t('common.all');
  }
  return browseActiveCategories.value.join(', ');
});

const browsePlayerCountsLabel = computed(() => {
  if (browseActivePlayerCounts.value.length === 0) {
    return t('common.all');
  }
  return browseActivePlayerCounts.value
    .map((playerCountRange) => `${playerCountRange.min}-${playerCountRange.max}`)
    .join(', ');
});

const browseTagsLabel = computed(() => {
  if (browseActiveTags.value.length === 0) {
    return t('common.all');
  }
  return browseActiveTags.value.join(', ');
});

function updateBrowseActiveCategories(category) {
  if (browseActiveCategories.value.includes(category)) {
    browseActiveCategories.value = browseActiveCategories.value.filter((c) => c !== category);
  } else {
    browseActiveCategories.value.push(category);
  }
}

function updateBrowseActivePlayerCounts(playerCount) {
  if (browseActivePlayerCounts.value.includes(playerCount)) {
    browseActivePlayerCounts.value = browseActivePlayerCounts.value.filter((c) => c !== playerCount);
  } else {
    browseActivePlayerCounts.value.push(playerCount);
  }
}

function updateBrowseActiveTags(tag) {
  if (browseActiveTags.value.includes(tag)) {
    browseActiveTags.value = browseActiveTags.value.filter((name) => name !== tag);
  } else {
    browseActiveTags.value.push(tag);
  }
}

const activeServer = computed(() => store.state.activeServer);

const buttonText = computed(() => (activeServer.value?.id && activeServer.value?.status === 'online' ? t('play.play_now.launch_mode') : t('play.shared.start_server')));

const mods = computed(() => store.state.modInfo?.mods?.filter((mod) => mod.playable && mod.cs2) || []);

const maps = computed(() => {
  if (selectedMod.value?.cs_maps?.length > 0) {
    return selectedMod.value.cs_maps;
  }
  const allMaps = mods.value?.[0]?.cs_maps || [];
  return allMaps;
});

const filteredMaps = computed(() => maps.value?.filter((map) => map.cs2 && map.friendly_name.toLowerCase().includes(mapsSearchQuery.value.toLowerCase())).sort((a, b) => a.friendly_name.localeCompare(b.friendly_name)) || []);

const filteredLocations = computed(() => sortByLatency(
  serverLocations.value.filter((location) => (
    location.name.toLowerCase().includes(locationsSearchQuery.value.toLowerCase())
  )),
));

const allStats = ref([]);
const statsLoading = ref(true);

const fiveWorstStats = computed(() => {
  const orderedStats = orderStatsByPerformance(allStats.value);
  return orderedStats.slice(-5); // Last 5 (worst)
});

const fiveBestStats = computed(() => {
  const orderedStats = orderStatsByPerformance(allStats.value);
  return orderedStats.slice(0, 5); // First 5 (best)
});

// Whether one of these stats gives PlayNowForYou a card: a weakness or strength
// (per `isCandidate`) whose recommended mods include one in the mod list.
function hasPlayableRecommendation(stats, isCandidate) {
  return stats.some((stat) => isCandidate(parseFloat(stat.data.percent_difference))
    && stat.data.recommended_mods?.some((recommended) => {
      const name = recommended?.modifier?.modifier_name?.toLowerCase();

      return !!name && mods.value.some((mod) => mod?.name?.toLowerCase() === name);
    }));
}

// Whether the Coach tab has a card to show. Having stats is not enough: a player
// with no matches still gets every stat, all zero and with no peer average to
// compare to, so none is a weakness or a strength and the tab is only its empty
// state -- which must not hold them there instead of falling back to Browse.
const hasCoachContent = computed(() => {
  // Until the profile and the mod list are both in, assume there is content, so
  // the page does not leave the tab before it can tell.
  if (statsLoading.value || !store.state.modInfo) return true;

  return hasPlayableRecommendation(fiveWorstStats.value, (pct) => pct < 0)
    || hasPlayableRecommendation(fiveBestStats.value, (pct) => pct > 0);
});

// Check if Favorites page has any content
const hasFavoritesContent = computed(() => {
  // While favorites are loading, assume we might have content to prevent premature redirect
  if (preferencesLoading.value) return true;

  return favoriteModIds.value.length > 0;
});

// Computed property to determine the best default tab
const defaultTab = computed(() => {
  if (hasCoachContent.value) {
    return 'coach';
  }

  if (hasFavoritesContent.value) {
    return 'favorites';
  }

  return 'browse';
});

function orderStatsByPerformance(stats) {
  if (!stats || typeof stats !== 'object') {
    return [];
  }

  const orderedStats = [];

  Object.keys(stats).forEach((statKey) => {
    const stat = stats[statKey];

    // rsparse sends every overview column with an empty mod list; only stats with a
    // mod to practise are candidates (see coach_weaknesses.js).
    const hasMods = !!stat?.recommended_mods?.length;

    if (statKey !== 'ct_side' && statKey !== 't_side' && stat?.deviations_away !== undefined && hasMods) {
      const playersValue = stat.players_value;
      const averageValue = stat.rating_range_average_value;
      let percentageDifference = 0;

      // null when there is no peer bracket to compare to.
      if (averageValue != null && averageValue !== 0) {
        if (stat.is_ascending) {
          percentageDifference = ((playersValue - averageValue) / averageValue) * 100;
        } else {
          percentageDifference = ((averageValue - playersValue) / averageValue) * 100;
        }
      }

      stat.percent_difference = percentageDifference.toFixed(2);
      stat.is_negative = percentageDifference < 0;
      // rsparse keys carry the aggregation (`time_to_kill_average`); the title is the label.
      const title = rsparseStatLabel(statKey);
      orderedStats.push({ title, data: stat });
    }
  });

  // Sort from best to worst (highest to lowest percentage difference)
  orderedStats.sort((a, b) => {
    const diffA = parseFloat(a.data.percent_difference);
    const diffB = parseFloat(b.data.percent_difference);
    return diffB - diffA;
  });

  return orderedStats;
}

function getMod(mode) {
  return mode?.toLowerCase();
}

function modCategoryIconSrc(category) {
  return new URL(`../assets/categories/circle/${category?.slug ?? 'misc'}.svg`, import.meta.url).href;
}

const selectedModIconSrc = computed(() => {
  for (const category of modCategories.value) {
    for (const m of category.mods) {
      if (selectedMod.value?.name === m.name) {
        return new URL(`../assets/categories/circle/${category?.slug ?? 'misc'}.svg`, import.meta.url).href;
      }
      if (m.children?.length > 0) {
        for (const child of m.children) {
          if (child.name === selectedMod.value?.name) {
            return new URL(`../assets/categories/circle/${category?.slug ?? 'misc'}.svg`, import.meta.url).href;
          }
        }
      }
    }
  }

  return new URL('../assets/categories/circle/misc.svg', import.meta.url).href;
});

const allMods = computed(() => {
  const mods = [];
  for (const category of modCategories.value) {
    for (const mod of category.mods) {
      if (mod.playable && mod.cs2 && (mod.name?.toLowerCase() !== 'scrim' || user.value?.access_level > 1)) {
        mods.push({
          mod,
          category,
        });
      }
      if (mod.children?.length > 0) {
        for (const childMod of mod.children) {
          if (childMod.cs2 && (childMod.name?.toLowerCase() !== 'scrim' || user.value?.access_level > 1)) {
            mods.push({
              mod: childMod,
              category,
            });
          }
        }
      }
    }
  }
  return mods.sort((a, b) => (a.mod?.friendly_name || '').localeCompare(b.mod?.friendly_name || ''));
});

function launchMode() {
  serverLoading.value = true;
  modeLaunched.value = false;

  if (!selectedMod.value?.name || !selectedMap.value?.name) {
    console.error('Selected mod or map name is undefined');
    store.commit('showMessage', {
      messageType: 'error',
      messageText: t('play.play_now.mod_or_map_missing'),
    });
    serverLoading.value = false;
    return;
  }

  axios({
    url: `/cs_servers/${activeServer.value.id}/exec_server_command`,
    method: 'POST',
    params: {
      command: 'change_map_and_mod',
      value: `${selectedMap.value.name}, ${getMod(selectedMod.value.name)}`,
    },
    headers: {
      'X-TEAM-ID': store.state.activeTeam.id,
    },
  })
    .then(() => {
      serverLoading.value = false;
      modeLaunched.value = true;

      // Reset the success state after 3 seconds
      setTimeout(() => {
        modeLaunched.value = false;
      }, 3000);
    })
    .catch((err) => {
      serverLoading.value = false;
      store.commit('showMessage', {
        messageType: 'error',
        messageText: t('play.shared.server_unreachable'),
      });
    });
}

function startServer() {
  // No catalogue means no region. Posting anyway sends no server_location_id,
  // `ServerLocation.find(nil)` raises, and it comes back as the generic failure
  // below -- which blames a server the user does not have running.
  //
  // Reload rather than "try again": an empty catalogue is held for the rest of
  // the page load, so clicking again asks nobody anything. Unless the
  // preferences are still in flight, which a reload would only restart.
  if (!selectedLocation.value) {
    store.commit('showMessage', {
      messageType: 'error',
      messageText: preferencesLoading.value
        ? t('play.shared.locations_still_loading')
        : t('play.play_now.locations_failed_reload'),
    });

    return;
  }

  serverLoading.value = true;
  axios({
    url: '/cs_servers/start_new_server',
    method: 'POST',
    timeout: 120000,
    headers: {
      'X-TEAM-ID': store.state.activeTeam.id,
    },
    data: {
      server_location_id: selectedLocation.value?.id,
      game: 'cs2',
      betaServer: betaServer.value,
      secureServer: false,
      is_assessment: false,
      launch_settings: {
        mod: getMod(selectedMod.value?.name),
        map: selectedMap.value?.name,
      },
    },
  })
    .then(({ data }) => {
      store.commit('setActiveServer', data);
      // A failed launch keeps it on so the player can retry.
      betaServer.value = false;
      setTimeout(() => {
        serverLoading.value = false;
        router.push('/server_manager');
      }, 2000);
    })
    .catch((err) => {
      serverLoading.value = false;
      if (err.code == 'ECONNABORTED') {
        store.commit('showMessage', {
          messageType: 'error',
          messageText: t('play.play_now.launch_timed_out'),
        });
        return;
      }
      store.commit('showMessage', {
        messageType: 'error',
        messageText: t('play.play_now.launch_failed'),
      });
    });
}

function handleServerAction() {
  if (activeServer.value?.id) {
    launchMode();
  } else {
    startServer();
  }
}

// Bumped per fetch so a pipeline flip mid-flight can't land the losing payload.
let coachDataRequestSeq = 0;

function getRefragCoachData() {
  statsLoading.value = true;
  coachDataRequestSeq += 1;
  const seq = coachDataRequestSeq;
  fetchCoachProfile(user.value)
    .then(({ stats }) => {
      if (seq !== coachDataRequestSeq) return;

      allStats.value = stats;
      statsLoading.value = false;
    })
    .catch((error) => {
      if (seq !== coachDataRequestSeq) return;

      console.error('Error fetching user profile data:', error);
      allStats.value = {};
      statsLoading.value = false;
    });
}

function selectMod(mod) {
  selectedMod.value = mod;

  // Check if the currently selected map exists in the new mod's available maps
  if (selectedMap.value && mod.cs_maps && mod.cs_maps.some((map) => map.name === selectedMap.value.name)) {
    // Keep the current map if it's available for this mod
    return;
  }

  // Otherwise, select the first available map for this mod
  selectedMap.value = mod.cs_maps?.[0] || null;
}

// The preferences and the mod list are independent requests landing in either
// order, and a stored mod is only an id: it cannot be resolved until both are
// in. Hence a watch on both rather than a check on the tick after either one.
const savedPlayNowApplied = ref(false);

function applySavedPlayNow() {
  if (savedPlayNowApplied.value || preferencesLoading.value) return;

  const savedModId = playNowModId.value;

  // Wait on `modInfo` landing, not on `mods` being non-empty: the latter never
  // comes for an account with nothing playable, and would block autoSelectMod
  // for good. With no id stored there is nothing to wait for.
  if (savedModId && !store.state.modInfo) return;

  // Before the early returns: autoSelectMod waits on this, and a stored mod that
  // is gone or off-limits must still let it choose.
  savedPlayNowApplied.value = true;

  if (!savedModId || route.query.game_mode) return;

  const savedMod = mods.value.find((mod) => mod.id === savedModId && mod.playable && mod.cs2);

  if (!savedMod) return;

  // Access can have been revoked since the mod was stored.
  if (savedMod.name.toLowerCase() === 'scrim' && (!user.value || user.value.access_level <= 1)) {
    updatePreferences({ play_now_mod_id: null, play_now_cs_map_id: null });
    return;
  }

  selectedMod.value = savedMod;

  // autoSelectMod bails as soon as a mod is set, so this is the only chance to
  // choose a map -- and a stored mod with no stored map, which the migration
  // produces, would otherwise start a server with no map at all.
  if (savedMod.cs_maps) {
    const savedMapId = playNowMapId.value;
    const savedMap = savedMapId && savedMod.cs_maps.find((map) => map.id === savedMapId && map.cs2);

    selectedMap.value = savedMap || savedMod.cs_maps.filter((map) => map.cs2)[0] || null;
  }
}

// What autoSelectMod filled the empty slots with: a watch cannot tell a click
// from an auto-pick, so the pick records itself here.
//
// It matters because the pick is derived from "nothing was stored", which a
// failed load cannot establish -- it leaves the same empty stand-in. A click is
// derived from nothing, so it persists either way.
let derivedModId = null;
let derivedMapId = null;

function saveSelectedMod(mod) {
  const id = mod?.id ?? null;

  if (id === derivedModId && !preferencesLoaded.value) return;
  if (id !== playNowModId.value) setPlayNowMod(mod);
}

function saveSelectedMap(map) {
  const id = map?.id ?? null;

  if (id === derivedMapId && !preferencesLoaded.value) return;
  if (id !== playNowMapId.value) setPlayNowMap(map);
}

function updateActiveTab(value) {
  activeTab.value = value;

  // Auto-scroll to selected mod when switching to browse tab
  if (value === 'browse' && selectedMod.value) {
    nextTick(() => {
      if (playNowBrowseRef.value) {
        // Add a delay to allow the component to fully render and filters to be applied
        setTimeout(() => {
          playNowBrowseRef.value.scrollToSelectedMod();
        }, 200);
      } else {
      }
    });
  } else {
  }
}

function updateUnderlineStyle() {
  nextTick(() => {
    const tabDiv = { coach: coachDiv, favorites: favoritesDiv, browse: browseDiv }[activeTab.value];

    if (!tabDiv?.value) return;

    sectionUnderlineWidth.value = tabDiv.value.offsetWidth;
    sectionUnderlinePadding.value = tabDiv.value.offsetLeft;
  });
}

// Handle window resize
function handleResize() {
  updateUnderlineStyle();
}

function getSectionUnderlineWidth() {
  return sectionUnderlineWidth.value;
}

function getSectionUnderlinePadding() {
  return sectionUnderlinePadding.value;
}

function updateSelectedMap(map) {
  selectedMap.value = map;
}

// Whether the selection below is a fallback rather than the stored region. A
// click stays put; a fallback gives way if the stored id turns up afterwards.
let locationDefaulted = false;

function updateSelectedLocation(location) {
  selectedLocation.value = location;
  locationDefaulted = false;
  setServerLocation(location);
}

function getServerLocations() {
  loadServerLocations();
  loadUserPreferences();
}

// Resolved from whatever the catalogue holds, not once when this page's own
// call settled: the shared load never rejects -- it resolves on an empty list --
// so one transient failure would leave every picker here with no selection for
// the rest of the page.
//
// Watched on the stored id too, so a fallback taken while the preferences were
// unreadable gives way to the real region when a retry lands. Otherwise one blip
// pins the user to a region they never picked for the whole session.
watch([serverLocations, preferencesLoading, serverLocationId], () => {
  if (preferencesLoading.value) return;
  if (selectedLocation.value && !locationDefaulted) return;

  const resolved = resolveServerLocation(serverLocations.value);

  locationDefaulted = resolved?.id !== serverLocationId.value;
  selectedLocation.value = resolved;
}, { immediate: true });

function getModCategories() {
  axios({
    method: 'get',
    url: '/mod_categories',
    headers: {
      'X-TEAM-ID': store.state.activeTeam.id,
    },
  }).then(({ data }) => {
    modCategories.value = data.mod_categories;
  });
}

function autoSelectMod() {
  // Wait for the next tick to ensure components are mounted
  nextTick(() => {
    // The stored mod gets first refusal. Choosing before it has been applied
    // takes the slot it wants, and the watch on selectedMod then writes the
    // auto-pick back over it.
    if (!savedPlayNowApplied.value) {
      return;
    }

    const linkQuery = route.query;
    const clearLinkQuery = () => {
      if (linkQuery.game_mode || linkQuery.map || linkQuery.cs_map) {
        router.replace({ path: '/play' });
      }
    };

    // Don't auto-select if we already have a mod (from saved preferences)
    if (selectedMod.value) {
      clearLinkQuery();
      return;
    }

    let autoMod = null;

    // Priority 0: Try to get mod from Query String
    if (linkQuery.game_mode) {
      const gameMode = currentModName(linkQuery.game_mode);
      autoMod = mods.value.find((mod) => mod.name.toLowerCase() === gameMode.toLowerCase() && mod.playable && mod.cs2);
      if (!autoMod && !mods.value?.length) {
        return; // Wait for mod watch to re-trigger the autoSelectMod() function.
      }
    }

    // Priority 1: Try to get mod from favorites if favorites has content
    if (!autoMod && playNowFavoritesRef.value && hasFavoritesContent.value) {
      autoMod = playNowFavoritesRef.value.getAutoSelectionMod();
    }

    // Priority 2: Try to get mod from coach recommendations
    if (!autoMod && playNowForYouRef.value && hasCoachContent.value) {
      autoMod = playNowForYouRef.value.getAutoSelectionMod();
    }

    // Priority 3: the first mod Browse lists. The categories carry every mod, so
    // skip what Browse leaves out: unplayable, not CS2, and Scrim without access.
    if (!autoMod && modCategories.value?.length > 0) {
      const canScrim = user.value?.access_level > 1;

      autoMod = modCategories.value
        .flatMap((category) => category.mods || [])
        .find((mod) => mod?.playable && mod.cs2 && (canScrim || mod.name?.toLowerCase() !== 'scrim')) || null;
    }

    if (autoMod) {
      derivedModId = autoMod.id ?? null;
      selectedMod.value = autoMod;

      const mapName = linkQuery.map || linkQuery.cs_map;
      if (mapName) {
        const map = autoMod.cs_maps?.find((map) => map.name.toLowerCase() === mapName.toLowerCase() && map.cs2);
        if (map) {
          selectedMap.value = map;
        } else {
          selectedMap.value = autoMod.cs_maps?.find((map) => map.cs2);
        }
      } else if (!selectedMap.value) { // Only set map if we don't already have one (from saved preferences)
        // A stored map with no stored mod still applies, if the mod chosen here
        // happens to have it.
        const savedMap = autoMod.cs_maps?.find((map) => map.id === playNowMapId.value && map.cs2);
        const autoMap = savedMap || autoMod.cs_maps?.find((map) => map.cs2);

        derivedMapId = autoMap?.id ?? null;
        selectedMap.value = autoMap;
      }

      clearLinkQuery();
    }
  });
}

useHead({ title: computed(() => t('play.play_now.page_title')) });

onMounted(() => {
  loadUserPreferences();

  getServerLocations();
  getRefragCoachData();
  getModCategories();
  // Auto-select mod after data is loaded
  autoSelectMod();
  // Add resize listener
  window.addEventListener('resize', handleResize);
  // Initial underline positioning
  updateUnderlineStyle();
});

onUnmounted(() => {
  // Clean up resize listener
  window.removeEventListener('resize', handleResize);
});

// Apply the stored mod as soon as both halves are in, whichever lands last.
watch([() => store.state.modInfo, preferencesLoading], applySavedPlayNow, { immediate: true });

watch([mods, modCategories, fiveWorstStats, fiveBestStats, savedPlayNowApplied], () => {
  // Auto-select mod when data changes
  autoSelectMod();
});

// Watch for changes in content availability and update default tab
watch([hasCoachContent, hasFavoritesContent, preferencesLoading], () => {
  if (preferencesLoading.value) return;

  const newDefaultTab = defaultTab.value;

  // Check if current tab has no content, then switch to new default
  if ((activeTab.value === 'coach' && !hasCoachContent.value)
      || (activeTab.value === 'favorites' && !hasFavoritesContent.value)) {
    applyDefaultTab(newDefaultTab);
  }
}, { immediate: true });

// Watch for activeTab changes to move the underline, wherever the change came
// from: a click, the saved tab arriving, or the fallback below.
watch(() => activeTab.value, () => {
  updateUnderlineStyle();
});

// Watch for changes in selectedMod and save to localStorage
watch(selectedMod, (newMod) => {
  if (newMod) {
    saveSelectedMod(newMod);
  }
});

// Watch for changes in selectedMap and save to localStorage
watch(selectedMap, (newMap) => {
  if (newMap) {
    saveSelectedMap(newMap);
  }
});

// Watch for changes in user access level and clear scrim mod if needed
watch(() => user.value?.access_level, (newAccessLevel, oldAccessLevel) => {
  // If access level decreased to 1 or below and current mod is scrim, clear it
  if (newAccessLevel <= 1 && selectedMod.value && selectedMod.value.name.toLowerCase() === 'scrim') {
    selectedMod.value = null;
    selectedMap.value = null;
    updatePreferences({ play_now_mod_id: null, play_now_cs_map_id: null });
    // Let autoSelectMod pick a new mod
    autoSelectMod();
  }
});

</script>

<style scoped>
.fade-bg {
  background: linear-gradient(180deg, rgba(8, 10, 13, 0.00) 2.21%, rgba(8, 10, 13, 0.100) 51.1%, #080A0D 100%);
}
</style>
